import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../config/supabase';
import { useAuth } from '../hooks/useAuth';
import { useChallengeNotification } from '../contexts/ChallengeNotificationContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  LinearProgress,
  Badge,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  BookOutlined,
  CalendarToday,
  ExpandMore,
  ExpandLess,
  Mood,
  MoodBad,
  SentimentSatisfied,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
  TrendingUp,
  EmojiEvents,
  LocalFireDepartment,
  Psychology,
  AutoAwesome,
  Star,
  MoreVert,
  FlashOn,
  Analytics,
  Settings,
  Logout,
  MonetizationOn,
  People,
  SportsEsports,
  EmojiObjects,
  Archive
} from '@mui/icons-material';

const Journal = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { showChallengeCompletion } = useChallengeNotification();
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newEntry, setNewEntry] = useState('');
  const [moodRating, setMoodRating] = useState(5);
  const [showNewEntryDialog, setShowNewEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editText, setEditText] = useState('');
  const [editMood, setEditMood] = useState(5);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [expandedEntries, setExpandedEntries] = useState(new Set()); // Track expanded journal entries
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const fetchJournalEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJournalEntries(data || []);
    } catch (err) {
      console.error('Error fetching journal entries:', err);
      setError('Failed to load journal entries');
    }
  };

  const fetchTokenBalance = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('token_balance')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;
      setTokenBalance(profile.token_balance);
    } catch (err) {
      console.error('Error fetching token balance:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.user?.id) return;
      
      try {
        setLoading(true);
        
        // Fetch journal entries
        const { data: journalData, error: journalError } = await supabase
          .from('journal_entries')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (journalError) throw journalError;
        setJournalEntries(journalData || []);

        // Fetch token balance
        const { data: profile, error: tokenError } = await supabase
          .from('user_profiles')
          .select('token_balance')
          .eq('id', session.user.id)
          .single();

        if (tokenError) throw tokenError;
        setTokenBalance(profile.token_balance);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [session?.user?.id]);

  const handleSaveEntry = async () => {
    if (!newEntry.trim()) {
      setError('Please write something in your journal entry');
      return;
    }

    // Check and deduct tokens
    const tokensRequired = 1; // Assuming each entry costs 1 token
    const tokensDeducted = await checkAndDeductTokens(tokensRequired);
    if (!tokensDeducted) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('journal_entries')
        .insert([
          {
            user_id: session.user.id,
            entry_text: newEntry.trim(),
            mood_rating: moodRating,
            entry_date: new Date().toISOString().split('T')[0]
          }
        ]);

      if (error) throw error;

      setSuccess('Journal entry saved successfully! (1 token used)');
      setNewEntry('');
      setMoodRating(5);
      setShowNewEntryDialog(false);
      fetchJournalEntries();
      
      // Notify backend about the journal entry for challenge progress
      await notifyBackendOfActivity('journal');
      
      // Backend notification handles challenge progress updates
    } catch (err) {
      console.error('Error saving journal entry:', err);
      setError('Failed to save journal entry');
      
      // Refund token if save failed
      try {
        await supabase
          .from('user_profiles')
          .update({ token_balance: tokenBalance + 1 })
          .eq('id', session.user.id);
        setTokenBalance(tokenBalance + 1);
      } catch (refundError) {
        console.error('Error refunding token:', refundError);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry.id);
    setEditText(entry.entry_text);
    setEditMood(entry.mood_rating);
  };

  const handleUpdateEntry = async () => {
    if (!editText.trim()) {
      setError('Please write something in your journal entry');
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase
        .from('journal_entries')
        .update({
          entry_text: editText.trim(),
          mood_rating: editMood,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingEntry);

      if (error) throw error;

      setSuccess('Journal entry updated successfully!');
      setEditingEntry(null);
      setEditText('');
      setEditMood(5);
      fetchJournalEntries();
    } catch (err) {
      console.error('Error updating journal entry:', err);
      setError('Failed to update journal entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this journal entry?')) {
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase
        .from('journal_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      setSuccess('Journal entry deleted successfully!');
      fetchJournalEntries();
    } catch (err) {
      console.error('Error deleting journal entry:', err);
      setError('Failed to delete journal entry');
    } finally {
      setSaving(false);
    }
  };

  const toggleEntryExpansion = (entryId) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const getMoodIcon = (rating) => {
    if (rating <= 2) return <SentimentVeryDissatisfied color="error" />;
    if (rating <= 4) return <MoodBad color="warning" />;
    if (rating <= 6) return <SentimentSatisfied color="info" />;
    if (rating <= 8) return <Mood color="success" />;
    return <SentimentVerySatisfied color="success" />;
  };

  const getMoodColor = (rating) => {
    if (rating <= 2) return 'error';
    if (rating <= 4) return 'warning';
    if (rating <= 6) return 'info';
    if (rating <= 8) return 'success';
    return 'success';
  };

  const groupEntriesByDate = (entries) => {
    const grouped = entries.reduce((acc, entry) => {
      const date = entry.entry_date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(entry);
      return acc;
    }, {});

    return Object.entries(grouped).map(([date, entries]) => ({
      date,
      entries
    }));
  };

  const getStreakCount = () => {
    if (!journalEntries || journalEntries.length === 0) return 0;

    // Get unique dates with entries, sorted from most recent
    const uniqueDates = [...new Set(journalEntries.map(entry => entry.entry_date))]
      .sort((a, b) => new Date(b) - new Date(a));

    if (uniqueDates.length === 0) return 0;

    let streak = 0;
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    const yesterdayStr = format(new Date(today.getTime() - 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

    // Check if we have an entry today or yesterday to start the streak
    if (uniqueDates.includes(todayStr)) {
      streak = 1;
      // Continue counting consecutive days backwards from today
      for (let i = 1; i < uniqueDates.length; i++) {
        const expectedDate = format(new Date(today.getTime() - i * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
        if (uniqueDates.includes(expectedDate)) {
          streak++;
        } else {
          break;
        }
      }
    } else if (uniqueDates.includes(yesterdayStr)) {
      // If no entry today but there's one yesterday, start from yesterday
      streak = 1;
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      for (let i = 1; i < uniqueDates.length; i++) {
        const expectedDate = format(new Date(yesterday.getTime() - (i - 1) * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
        if (uniqueDates.includes(expectedDate)) {
          if (i > 1) streak++; // Don't double count yesterday
        } else {
          break;
        }
      }
    }

    return streak;
  };

  const checkAndDeductTokens = async (tokensToDeduct) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('token_balance')
        .eq('id', session.user.id)
        .single();

      if (profileError) throw profileError;

      if (profile.token_balance < tokensToDeduct) {
        setError(`Insufficient tokens. You need ${tokensToDeduct} tokens but only have ${profile.token_balance}. Click here to get more tokens.`);
        setTimeout(() => {
          navigate('/get-tokens');
        }, 3000);
        return false;
      }

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ token_balance: profile.token_balance - tokensToDeduct })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      setTokenBalance(profile.token_balance - tokensToDeduct);
      return true;
    } catch (err) {
      console.error('Error checking/deducting tokens:', err);
      setError('Failed to process token deduction');
      return false;
    }
  };

  const notifyBackendOfActivity = async (activityType) => {
    try {
      const response = await fetch('https://backend.sanityreps.com/api/challenges/force-update-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          activityType: activityType === 'journal' ? 'journal_entry' : activityType
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check multiple possible locations for challenge completion data
        const challengesCompleted = data.challengesCompleted || 
                                   data.result?.challengesCompleted || 
                                   data.result?.challenges_completed ||
                                   [];
        
        
        // Only show modal if challenges were actually completed
        if (challengesCompleted.length > 0) {
          
          // Update user's total XP in database for each completed challenge
          for (const completedChallenge of challengesCompleted) {
            const xpReward = completedChallenge.xp_reward || 50;
            
            try {
              // Add XP to user's total_xp in user_profiles table
              const { error: xpError } = await supabase.rpc('increment_user_xp', {
                user_id: session.user.id,
                xp_amount: xpReward
              });

              if (xpError) {
                console.error('Error updating user XP:', xpError);
                // Fallback: update directly if RPC doesn't exist
                const { data: currentProfile, error: fetchError } = await supabase
                  .from('user_profiles')
                  .select('total_xp')
                  .eq('id', session.user.id)
                  .single();

                if (!fetchError && currentProfile) {
                  const { error: updateError } = await supabase
                    .from('user_profiles')
                    .update({ total_xp: (currentProfile.total_xp || 0) + xpReward })
                    .eq('id', session.user.id);

                  if (updateError) {
                    console.error('Error updating XP directly:', updateError);
                  } else {
                  }
                }
              } else {
              }
            } catch (xpUpdateError) {
              console.error('Error updating user XP:', xpUpdateError);
            }
            
            // Show modal for completed challenge
            showChallengeCompletion(
              completedChallenge.title,
              xpReward,
              'journal'
            );
          }
        }
      } else {
      }
    } catch (error) {
      // Don't throw error - this is not critical functionality
    }
  };

  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogoutFromMenu = async () => {
    handleMenuClose();
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your journal...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: '#fafbfc', 
      minHeight: '100vh',
      '& @keyframes pulse': {
        '0%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.7, transform: 'scale(1.1)' },
        '100%': { opacity: 1, transform: 'scale(1)' }
      }
    }}>
      {/* Progress Dashboard Style Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e4e7',
        py: 3
      }}>
        <Container maxWidth="xl">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton 
                onClick={() => navigate('/conversation')}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' }
                }}
              >
                <ArrowBack />
              </IconButton>
              
              <Box>
                <Typography variant="h4" fontWeight="600" color="#1a1a1a">
                  Journal Dashboard
                </Typography>
                <Typography variant="body1" color="#6b7280" sx={{ mt: 0.5 }}>
                  📖 Reflect, track your mood, and celebrate your growth
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate('/challenges')}
                startIcon={<EmojiEvents />}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    borderColor: '#5a67d8',
                    backgroundColor: 'rgba(102, 126, 234, 0.05)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                  },
                  transition: 'all 0.2s ease',
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Start Challenge
              </Button>
              
              {/* Mobile Challenge Button */}
              <IconButton
                onClick={() => navigate('/challenges')}
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  display: { xs: 'flex', sm: 'none' },
                  '&:hover': { 
                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                title="Start Challenge"
              >
                <EmojiEvents />
              </IconButton>
              
              <Badge badgeContent={journalEntries.length} color="primary">
                <IconButton 
                  onClick={handleMenuOpen}
                  sx={{ 
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' }
                  }}
                >
                  <MoreVert />
                </IconButton>
              </Badge>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          
          {/* Gamified Stats Row */}
          <Grid item xs={4} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
                  <Box sx={{ 
                    width: { xs: 40, sm: 48 }, 
                    height: { xs: 40, sm: 48 }, 
                    borderRadius: 2,
                    bgcolor: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    animation: 'pulse 2s infinite'
                  }}>
                    <BookOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="700" color="#1a1a1a"
                             fontSize={{ xs: '1.5rem', sm: '2.5rem', md: '3rem' }}>
                    {journalEntries.length}
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '0.9rem', sm: '1.25rem' }}>
                    Total Entries
                  </Typography>
                  <Typography variant="body2" color="#6b7280"
                             fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                    Keep writing! 📝
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
                  <Box sx={{ 
                    width: { xs: 40, sm: 48 }, 
                    height: { xs: 40, sm: 48 }, 
                    borderRadius: 2,
                    bgcolor: '#f093fb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    animation: 'pulse 2s infinite'
                  }}>
                    <Psychology sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="700" color="#1a1a1a"
                             fontSize={{ xs: '1.5rem', sm: '2.5rem', md: '3rem' }}>
                    {journalEntries.filter(e => e.mood_rating >= 7).length}
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '0.9rem', sm: '1.25rem' }}>
                    Good Days
                  </Typography>
                  <Typography variant="body2" color="#6b7280"
                             fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                    Mood 7+ entries 😊
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
                  <Box sx={{ 
                    width: { xs: 40, sm: 48 }, 
                    height: { xs: 40, sm: 48 }, 
                    borderRadius: 2,
                    bgcolor: '#4ecdc4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    animation: 'pulse 2s infinite'
                  }}>
                    <TrendingUp sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="700" color="#1a1a1a"
                             fontSize={{ xs: '1.5rem', sm: '2.5rem', md: '3rem' }}>
                    {journalEntries.length > 0 ? (journalEntries.reduce((sum, e) => sum + e.mood_rating, 0) / journalEntries.length).toFixed(1) : '0.0'}
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '0.9rem', sm: '1.25rem' }}>
                    Avg Mood
                  </Typography>
                  <Typography variant="body2" color="#6b7280"
                             fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                    Keep it up! 📈
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
                  <Box sx={{ 
                    width: { xs: 40, sm: 48 }, 
                    height: { xs: 40, sm: 48 }, 
                    borderRadius: 2,
                    bgcolor: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    animation: 'pulse 2s infinite'
                  }}>
                    <LocalFireDepartment sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" fontWeight="700" color="#1a1a1a"
                             fontSize={{ xs: '1.5rem', sm: '2.5rem', md: '3rem' }}>
                    {getStreakCount()}
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="#1a1a1a"
                             fontSize={{ xs: '0.9rem', sm: '1.25rem' }}>
                    Day Streak
                  </Typography>
                  <Typography variant="body2" color="#6b7280"
                             fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                    You're on fire! 🔥
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Journal Content */}
          <Grid item xs={12}>
            <Card sx={{ 
              bgcolor: 'white',
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e0e4e7',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' }
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography variant="h5" fontWeight="700" color="#1a1a1a">
                    Your Journal Entries
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<Add />}
                    onClick={() => setShowNewEntryDialog(true)} 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontWeight: 700,
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(102,126,234,0.25)',
                      '&:hover': { 
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    New Entry
                  </Button>
                </Box>
                
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                
                <Grid container spacing={2}>
                  {journalEntries.length === 0 && !loading && (
                    <Grid item xs={12}>
                      <Paper sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        borderRadius: 3,
                        boxShadow: '0 4px 12px rgba(240,147,251,0.25)'
                      }}>
                        <AutoAwesome sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h5" fontWeight="700" gutterBottom>
                          Start Your Journey! 🌟
                        </Typography>
                        <Typography variant="body1">
                          Your first journal entry is the beginning of something amazing!
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {journalEntries.map(entry => (
                    <Grid item xs={12} key={entry.id}>
                      <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e4e7',
                        borderLeft: `6px solid #667eea`,
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-2px)' }
                      }}>
                        {/* Collapsible Header */}
                        <CardContent 
                          sx={{ 
                            p: 2, 
                            cursor: 'pointer',
                            borderBottom: expandedEntries.has(entry.id) ? '1px solid #e0e4e7' : 'none'
                          }}
                          onClick={() => toggleEntryExpansion(entry.id)}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 2,
                              bgcolor: '#f093fb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 20
                            }}>
                              {getMoodIcon(entry.mood_rating)}
                            </Box>
                            <Box flexGrow={1}>
                              <Typography variant="body1" fontWeight={600} color="#1a1a1a">
                                {format(parseISO(entry.entry_date), 'PPP')} - Mood: {entry.mood_rating}/10
                              </Typography>
                              <Typography variant="caption" color="#6b7280">
                                {expandedEntries.has(entry.id) ? 'Click to collapse' : 'Click to expand entry'}
                              </Typography>
                            </Box>
                            <IconButton 
                              size="small"
                              sx={{ color: '#667eea' }}
                            >
                              {expandedEntries.has(entry.id) ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                        </CardContent>
                        
                        {/* Collapsible Content */}
                        <Collapse in={expandedEntries.has(entry.id)}>
                          <CardContent sx={{ p: 3, pt: 2 }}>
                            <Typography variant="body1" color="#1a1a1a" mb={2} sx={{ lineHeight: 1.6 }}>
                              {entry.entry_text}
                            </Typography>
                            <Box display="flex" justifyContent="flex-end" gap={1}>
                              <IconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditEntry(entry);
                                }}
                                sx={{ 
                                  bgcolor: 'rgba(102,126,234,0.1)',
                                  color: '#667eea',
                                  '&:hover': { bgcolor: 'rgba(102,126,234,0.2)' }
                                }}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEntry(entry.id);
                                }}
                                sx={{ 
                                  bgcolor: 'rgba(244,67,54,0.1)',
                                  color: '#f44336',
                                  '&:hover': { bgcolor: 'rgba(244,67,54,0.2)' }
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Collapse>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {loading && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <CircularProgress sx={{ color: '#667eea' }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* New Entry Dialog */}
        <Dialog 
          open={showNewEntryDialog} 
          onClose={() => setShowNewEntryDialog(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              m: { xs: 1, sm: 2 },
              maxHeight: { xs: '95vh', sm: '90vh' },
              borderRadius: { xs: 2, sm: 3 }
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3 }
          }}>
            ✨ New Journal Entry
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <TextField
              label="What's on your mind?"
              multiline
              rows={6}
              fullWidth
              value={newEntry}
              onChange={e => setNewEntry(e.target.value)}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafbfc'
                }
              }}
            />
            <Typography variant="h6" gutterBottom color="#1a1a1a" fontWeight={600}
                       fontSize={{ xs: '1rem', sm: '1.25rem' }} mb={2}>
              How are you feeling? (1-10)
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(5, 1fr)', sm: 'repeat(10, 1fr)' },
              gap: { xs: 1, sm: 1.5 },
              justifyItems: 'center',
              mb: 2,
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              {[1,2,3,4,5,6,7,8,9,10].map(rating => (
                <Box key={rating} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IconButton 
                    onClick={() => setMoodRating(rating)} 
                    sx={{ 
                      color: moodRating === rating ? '#f093fb' : '#e0e4e7',
                      fontSize: { xs: 24, sm: 32 },
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'scale(1.2)' },
                      p: { xs: 0.5, sm: 1 },
                      minWidth: { xs: 32, sm: 40 },
                      minHeight: { xs: 32, sm: 40 }
                    }}
                  >
                    {getMoodIcon(rating)}
                  </IconButton>
                  <Typography variant="caption" color="#6b7280" fontSize={{ xs: '0.65rem', sm: '0.75rem' }}>
                    {rating}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="#6b7280" textAlign="center" 
                       fontSize={{ xs: '0.875rem', sm: '1rem' }}>
              Current mood: {moodRating}/10 {getMoodIcon(moodRating)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
            <Button 
              onClick={() => setShowNewEntryDialog(false)}
              sx={{ color: '#6b7280', fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEntry} 
              variant="contained" 
              disabled={saving}
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 3 }
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Entry'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Edit Entry Dialog */}
        <Dialog 
          open={!!editingEntry} 
          onClose={() => setEditingEntry(null)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              m: { xs: 1, sm: 2 },
              maxHeight: { xs: '95vh', sm: '90vh' },
              borderRadius: { xs: 2, sm: 3 }
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3 }
          }}>
            ✏️ Edit Journal Entry
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <TextField
              label="Edit your entry"
              multiline
              rows={6}
              fullWidth
              value={editText}
              onChange={e => setEditText(e.target.value)}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafbfc'
                }
              }}
            />
            <Typography variant="h6" gutterBottom color="#1a1a1a" fontWeight={600}
                       fontSize={{ xs: '1rem', sm: '1.25rem' }} mb={2}>
              Update your mood (1-10)
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(5, 1fr)', sm: 'repeat(10, 1fr)' },
              gap: { xs: 1, sm: 1.5 },
              justifyItems: 'center',
              mb: 2,
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              {[1,2,3,4,5,6,7,8,9,10].map(rating => (
                <Box key={rating} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IconButton 
                    onClick={() => setEditMood(rating)} 
                    sx={{ 
                      color: editMood === rating ? '#f093fb' : '#e0e4e7',
                      fontSize: { xs: 24, sm: 32 },
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'scale(1.2)' },
                      p: { xs: 0.5, sm: 1 },
                      minWidth: { xs: 32, sm: 40 },
                      minHeight: { xs: 32, sm: 40 }
                    }}
                  >
                    {getMoodIcon(rating)}
                  </IconButton>
                  <Typography variant="caption" color="#6b7280" fontSize={{ xs: '0.65rem', sm: '0.75rem' }}>
                    {rating}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="#6b7280" textAlign="center"
                       fontSize={{ xs: '0.875rem', sm: '1rem' }}>
              Current mood: {editMood}/10 {getMoodIcon(editMood)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
            <Button 
              onClick={() => setEditingEntry(null)}
              sx={{ color: '#6b7280', fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateEntry} 
              variant="contained" 
              disabled={saving}
              sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 3 },
                py: 1,
                borderRadius: 2,
                '&:hover': { 
                  background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)'
                }
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Update Entry'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menu */}
        <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleMenuClose}>
          {/* Dopamine-producing links at the top */}
          <MenuItem onClick={() => handleMenuItemClick('/get-tokens')}>
            <ListItemIcon><MonetizationOn sx={{ color: '#FFD700' }} /></ListItemIcon>
            <ListItemText primary="Get Tokens" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/human-therapists')}>
            <ListItemIcon><People sx={{ color: '#4CAF50' }} /></ListItemIcon>
            <ListItemText primary="Human Therapists" />
          </MenuItem>
          <Divider />
          
          {/* Core features */}
          <MenuItem onClick={() => handleMenuItemClick('/conversation')}>
            <ListItemIcon><Psychology sx={{ color: '#E91E63' }} /></ListItemIcon>
            <ListItemText primary="Chat" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/journal')}>
            <ListItemIcon><BookOutlined sx={{ color: '#2196F3' }} /></ListItemIcon>
            <ListItemText primary="Journal" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/instant-wellness')}>
            <ListItemIcon><FlashOn sx={{ color: '#FF9800' }} /></ListItemIcon>
            <ListItemText primary="Instant Wellness" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/intelligent-meditation')}>
            <ListItemIcon><Psychology sx={{ color: '#9C27B0' }} /></ListItemIcon>
            <ListItemText primary="Meditation" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/progress')}> 
            <ListItemIcon><Analytics sx={{ color: '#00BCD4' }} /></ListItemIcon>
            <ListItemText primary="Progress" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/challenges')}>
            <ListItemIcon><SportsEsports sx={{ color: '#FF5722' }} /></ListItemIcon>
            <ListItemText primary="Challenges" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/mind-games')}>
            <ListItemIcon><EmojiObjects sx={{ color: '#9C27B0' }} /></ListItemIcon>
            <ListItemText primary="Mind Games" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/leaderboard')}>
            <ListItemIcon><EmojiEvents sx={{ color: '#FFC107' }} /></ListItemIcon>
            <ListItemText primary="Leaderboard" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/conversation')}>
            <ListItemIcon><Settings sx={{ color: '#757575' }} /></ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogoutFromMenu}>
            <ListItemIcon><Logout sx={{ color: '#F44336' }} /></ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};

export default Journal;
